/**
 * GRAFİK SERİSİ ÖNBELLEĞİ (kullanıcı, 12.10.2026: "tarih değişmediyse, yeni
 * hareket girilmediyse verinin bir kere çekilmiş olması yeterli; yalnızca
 * daha önce gelmemiş olan çekilsin").
 *
 * Her gün bir tam değerleme olduğu için bir yıllık seri ~10 sn sürüyor.
 * Hesaplanan günler burada, para birimine göre saklanır; grafik yalnızca
 * EKSİK günleri motordan ister.
 *
 * NE ZAMAN SİLİNİR:
 *   - Deftere dokunan her değişiklik (hareket, hesap, elle fiyat/değer, içe
 *     aktarma, geri yükleme): HEPSİ (`defterDegisti`). Uygulamada depo
 *     bağlamının `invalidate`ı bunu çağırır.
 *   - Eşitleme yeni ya da değişmiş fiyat/kur/olay yazdıysa: o günden SONRASI
 *     (`fiyatlarDegisti`; günü fiyat önbelleği söyler, `ChangedFrom`).
 *     Aynı fiyatı yeniden yazmak değişiklik değildir → hiçbir şey silinmez.
 *   - Kilit, profil değişimi, sayfa yenileme: sayfa yeniden yüklenir, bellek
 *     gider. KALICI KOPYA (12.10.2026, cihaz v8): aynı günler profilin
 *     ŞİFRELİ kabında da durur (`LocalRepository.seriOnbellegi`); bellekte
 *     olmayan gün önce oradan okunur (`kalicidanTamamla`). Kaptaki kopyayı
 *     defter ve bar/kur değişiminde veritabanı tetikleyicileri siler;
 *     künye/olay değişimini eşitleme `sil` ile bildirir.
 *
 * SON GÜNLER SAKLANMAZ: bugün ve önceki `SAKLANMAYAN_GUN - 1` gün her seferinde
 * yeniden hesaplanır. Sebebi: o günlerin "bayat mı" hükmü SAATE bağlı
 * (pazartesi 09:00'da cuma fiyatı taze, 14:00'te bayat) ve bugünün değeri
 * anlık fiyatla gün içinde değişir. Birkaç günlük hesap yarım saniyenin altında.
 */

import type { IsoDate } from "@parafon/contracts";
import type { ChangedFrom } from "@parafon/contracts/device-api";
import type { LocalRepository, ValuationSeriesPoint } from "@parafon/contracts/local-api";

export const SAKLANMAYAN_GUN = 7;

type Gunler = Map<IsoDate, ValuationSeriesPoint>;

/** Tek depo açık olur (profil değişimi = sayfa yenilemesi); yine de depoya bağlı. */
let durum: { readonly repo: object; readonly paralar: Map<string, Gunler> } | null = null;

function paralar(repo: object): Map<string, Gunler> {
  if (durum === null || durum.repo !== repo) durum = { repo, paralar: new Map() };
  return durum.paralar;
}

function gunEkle(date: IsoDate, gun: number): IsoDate {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + gun);
  return d.toISOString().slice(0, 10);
}

/** Saklanabilecek en geç gün (bugün − SAKLANMAYAN_GUN). */
export function saklanabilirSon(bugun: IsoDate): IsoDate {
  return gunEkle(bugun, -SAKLANMAYAN_GUN);
}

export interface OnbellekDurumu {
  /** Aralıkta önbellekte olan günler. */
  readonly elde: ReadonlyMap<IsoDate, ValuationSeriesPoint>;
  /** Motordan istenmesi gereken bitişik aralıklar (sıralı). */
  readonly eksikler: readonly (readonly [IsoDate, IsoDate])[];
  readonly eksikGun: number;
}

export function onbellekDurumu(
  repo: object,
  para: string,
  from: IsoDate,
  to: IsoDate,
  bugun: IsoDate,
): OnbellekDurumu {
  const gunler = paralar(repo).get(para);
  const sinir = saklanabilirSon(bugun);
  const elde = new Map<IsoDate, ValuationSeriesPoint>();
  const eksikler: [IsoDate, IsoDate][] = [];
  let eksikGun = 0;
  let acik: IsoDate | null = null;
  let onceki: IsoDate | null = null;
  for (let d = from; d <= to; d = gunEkle(d, 1)) {
    const p = d <= sinir ? gunler?.get(d) : undefined;
    if (p !== undefined) {
      elde.set(d, p);
      if (acik !== null && onceki !== null) eksikler.push([acik, onceki]);
      acik = null;
    } else {
      eksikGun += 1;
      if (acik === null) acik = d;
    }
    onceki = d;
  }
  if (acik !== null && onceki !== null) eksikler.push([acik, onceki]);
  return { elde, eksikler, eksikGun };
}

/** Saklanabilecek noktalar (son günler hariç). */
export function saklanabilirler(
  points: readonly ValuationSeriesPoint[],
  bugun: IsoDate,
): ValuationSeriesPoint[] {
  const sinir = saklanabilirSon(bugun);
  return points.filter((p) => p.date <= sinir);
}

/**
 * Bellekte eksik günleri şifreli kaptaki kalıcı kopyadan tamamlar. Kalıcı
 * kopya yoksa (sahte depo, eski Worker) ya da okunamazsa sessizce geçer:
 * eksik gün yine motordan hesaplanır.
 */
export async function kalicidanTamamla(
  repo: LocalRepository,
  para: string,
  eksikler: readonly (readonly [IsoDate, IsoDate])[],
  bugun: IsoDate,
): Promise<void> {
  const kalici = repo.seriOnbellegi;
  if (kalici === undefined) return;
  for (const [a, b] of eksikler) {
    const son = b < saklanabilirSon(bugun) ? b : saklanabilirSon(bugun);
    if (a > son) continue;
    try {
      seriyiSakla(repo, para, await kalici.oku(para, a, son), bugun);
    } catch {
      return;
    }
  }
}

/** Hesaplanan noktaları saklar — son günler HARİÇ (yukarıdaki not). */
export function seriyiSakla(
  repo: object,
  para: string,
  points: readonly ValuationSeriesPoint[],
  bugun: IsoDate,
): void {
  const p = paralar(repo);
  let gunler = p.get(para);
  if (gunler === undefined) {
    gunler = new Map();
    p.set(para, gunler);
  }
  const sinir = saklanabilirSon(bugun);
  for (const nokta of points) if (nokta.date <= sinir) gunler.set(nokta.date, nokta);
}

/** Defter değişti: her şey yeniden hesaplanır. */
export function defterDegisti(): void {
  durum = null;
}

/**
 * Fiyat önbelleği değişti: o günden sonrası atılır. `undefined` (motor
 * söylemedi) ve "all" → hepsi; `null` → hiçbir şey.
 */
export function fiyatlarDegisti(changedFrom: ChangedFrom | undefined): void {
  if (changedFrom === null || durum === null) return;
  if (changedFrom === undefined || changedFrom === "all") {
    durum = null;
    return;
  }
  for (const gunler of durum.paralar.values()) {
    for (const d of [...gunler.keys()]) if (d >= changedFrom) gunler.delete(d);
  }
}
